import React from 'react';
import type { HirelingHealEntry, RestSummaryDTO } from '@hexcrawl/shared-utils';

interface RestResultOverlayProps {
  summary: RestSummaryDTO;
  onDismiss: () => void;
}

export const RestResultOverlay: React.FC<RestResultOverlayProps> = ({ summary, onDismiss }) => {
  const isInn = summary.restType === 'inn';
  const noRecovery = !isInn && summary.foodConsumed === 0;

  const title = isInn ? 'Stayed at Inn' : 'Made Camp';
  const borderColor = isInn ? '#ffd700' : '#5aa0ff';

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.6)',
      zIndex: 5000,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontFamily: 'monospace',
    }}>
      <div style={{
        background: '#0e2238',
        border: `2px solid ${borderColor}`,
        borderRadius: 12,
        padding: '24px 36px',
        textAlign: 'center',
        minWidth: 280,
        maxWidth: 400,
      }}>
        <h2 style={{
          color: isInn ? '#ffd700' : '#a8d1ff',
          fontSize: 20,
          margin: '0 0 4px 0',
        }}>
          {title}
        </h2>
        <div style={{ color: '#7799bb', fontSize: 12, marginBottom: 16 }}>
          Day {summary.newDay}
        </div>

        {/* Placeholder for future campsite/inn graphic */}

        <div style={{
          background: '#0a1a2a',
          border: '1px solid #1a3a5a',
          borderRadius: 8,
          padding: '12px 16px',
          marginBottom: 20,
          textAlign: 'left',
        }}>
          {noRecovery ? (
            <div style={{ color: '#ff8866', fontSize: 14, textAlign: 'center' }}>
              No food! No recovery.
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '6px 12px', fontSize: 14 }}>
              {summary.hpRestored > 0 && <>
                <span style={{ color: '#88ff88', fontWeight: 'bold', textAlign: 'right' }}>+{summary.hpRestored}</span>
                <span style={{ color: '#aaa' }}>HP restored</span>
              </>}
              {summary.mpRestored > 0 && <>
                <span style={{ color: '#88ccff', fontWeight: 'bold', textAlign: 'right' }}>+{summary.mpRestored}</span>
                <span style={{ color: '#aaa' }}>MP restored</span>
              </>}
              {summary.hpRestored === 0 && summary.mpRestored === 0 && (
                <span style={{ color: '#aaa', gridColumn: '1 / -1', textAlign: 'center' }}>Already at full health.</span>
              )}
            </div>
          )}
        </div>

        {summary.hirelingHeals && summary.hirelingHeals.length > 0 && (
          <div style={{
            background: '#0a1a2a',
            border: '1px solid #1a3a5a',
            borderRadius: 8,
            padding: '10px 16px',
            marginBottom: 12,
            textAlign: 'left',
          }}>
            <div style={{ color: '#667788', fontSize: 11, marginBottom: 6 }}>Party</div>
            {summary.hirelingHeals.map((entry: HirelingHealEntry) => (
              <HirelingHealRow key={entry.npcId} entry={entry} />
            ))}
          </div>
        )}

        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: '4px 16px',
          fontSize: 12,
          color: '#667788',
          marginBottom: 20,
          textAlign: 'left',
        }}>
          {isInn && summary.goldSpent > 0 && <>
            <span>Gold spent:</span>
            <span style={{ color: '#ffd700' }}>{summary.goldSpent}</span>
          </>}
          {!isInn && summary.foodConsumed > 0 && <>
            <span>Food consumed:</span>
            <span style={{ color: '#ffcc66' }}>{summary.foodConsumed}</span>
          </>}
          <span>Movement restored</span>
          <span style={{ color: '#cc99ff' }}>Full</span>
        </div>

        <button
          onClick={onDismiss}
          style={{
            background: isInn ? '#3a2a1a' : '#1a3a5a',
            color: isInn ? '#ffd700' : '#a8d1ff',
            border: `1px solid ${borderColor}`,
            borderRadius: 6,
            padding: '8px 32px',
            fontFamily: 'monospace',
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          OK
        </button>
      </div>
    </div>
  );
};

const HirelingHealRow: React.FC<{ entry: HirelingHealEntry }> = ({ entry }) => {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, marginBottom: 4 }}>
      <span style={{ color: '#aabbcc' }}>{entry.npcName}</span>
      {entry.fed ? (
        <span>
          {entry.hpRestored > 0 && <span style={{ color: '#88ff88' }}>+{entry.hpRestored} HP </span>}
          {entry.mpRestored > 0 && <span style={{ color: '#88ccff' }}>+{entry.mpRestored} MP</span>}
          {entry.hpRestored === 0 && entry.mpRestored === 0 && <span style={{ color: '#aabbcc' }}>Full</span>}
        </span>
      ) : (
        <span style={{ color: '#ff8866' }}>No food</span>
      )}
    </div>
  );
};
